import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  useChat,
  type ChatMessage,
  type ChoiceOption,
  type LiveFlowItem,
  type FormQuestion,
  type FormAnswer,
  type FormAnswers,
  type FormAnswerText,
  type FormAnswerSingle,
  type FormAnswerMulti,
  CHOICE_OTHER_VALUE,
} from "@/hooks/useChat";
import { renderMarkdown } from "@/lib/markdown";
import { apiPostJson, apiFetch } from "@/lib/api";
import { SendIcon, StopIcon, PlusIcon, RetryIcon, AISparkleIcon } from "./Icon";
import { GenUIBlock } from "./GenUIBlock";
import { ProjectChipRow } from "./ProjectChip";
import { ProjectDetailModal } from "./ProjectDetailModal";
import { ReviewChipRow } from "./ReviewChip";
import { dedupeProjects } from "@/lib/project-mentions";
import { dedupeReviewRefs, type ReviewRef } from "@/lib/review-refs";
import { STORAGE_LEADS } from "@/constants/storage";

const LEAD_KEY = STORAGE_LEADS.leadSubmitted;

// Tool names that correspond to HITL pauses — these are shown via the
// pending/approval UI (or, for workspace_propose_*, the frozen
// WorkspaceProposeCard via propose_result rows), not as raw trace
// blocks in the conversation.
const HITL_TOOL_NAMES = new Set([
  "request_user_input",
  "request_user_choice",
  "request_user_form",
  "workspace_propose_shortlist",
  "workspace_propose_intent_update",
  "workspace_propose_preference_sync",
  "workspace_propose_dossier_note",
]);

import type { GenUIPayload } from "@/lib/gen-ui";

type ChoiceResultMsg = Extract<ChatMessage, { role: "choice_result" }>;
type FormResultMsg = Extract<ChatMessage, { role: "form_result" }>;
type ProposeResultMsg = Extract<ChatMessage, { role: "propose_result" }>;
type AssistantTurnItem =
  | { type: "trace"; name: string; projects?: string[]; refs?: ReviewRef[] }
  | { type: "text"; content: string; id?: string }
  | { type: "gen_ui"; payload: GenUIPayload };

type HistoryGroup =
  | { kind: "user"; msg: ChatMessage }
  | { kind: "error"; msg: ChatMessage }
  | { kind: "choice_result"; msg: ChoiceResultMsg }
  | { kind: "form_result"; msg: FormResultMsg }
  | { kind: "propose_result"; msg: ProposeResultMsg }
  | { kind: "assistant"; items: AssistantTurnItem[] };

function groupHistory(msgs: ChatMessage[]): HistoryGroup[] {
  const groups: HistoryGroup[] = [];
  for (const msg of msgs) {
    if (msg.role === "user") {
      groups.push({ kind: "user", msg });
    } else if (msg.role === "error") {
      groups.push({ kind: "error", msg });
    } else if (msg.role === "choice_result") {
      groups.push({ kind: "choice_result", msg });
    } else if (msg.role === "form_result") {
      groups.push({ kind: "form_result", msg });
    } else if (msg.role === "propose_result") {
      groups.push({ kind: "propose_result", msg });
    } else {
      const last = groups[groups.length - 1];
      let group: Extract<HistoryGroup, { kind: "assistant" }>;
      if (last?.kind === "assistant") {
        group = last;
      } else {
        group = { kind: "assistant", items: [] };
        groups.push(group);
      }
      if (msg.role === "tool_trace") {
        group.items.push({ type: "trace", name: msg.name, projects: msg.projects, refs: msg.refs });
      } else if (msg.role === "gen_ui") {
        group.items.push({ type: "gen_ui", payload: msg.payload });
      } else {
        const am = msg as { role: "assistant"; content: string; id?: string };
        group.items.push({ type: "text", content: am.content, id: am.id });
      }
    }
  }
  return groups;
}

export function ChatUI({ mode, conversationId, workspaceId }: { mode?: string; conversationId?: string | null; workspaceId?: string } = {}) {
  const { t } = useTranslation();
  const chat = useChat(mode, conversationId, workspaceId);
  const [input, setInput] = useState("");
  const [showTrace, setShowTrace] = useState(true);
  const [showLead, setShowLead] = useState(false);
  const [denyReason, setDenyReason] = useState("");
  const [openProject, setOpenProject] = useState<string | null>(null);
  const [headerToast, setHeaderToast] = useState<string | null>(null);
  const handleOpenProject = useCallback((name: string) => setOpenProject(name), []);
  const messagesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Empty-state chips and copy diverge by audience: consumers ask about ABSD / HDB
  // eligibility / yield, agents ask for listing copy / lead scoring / comps / followups.
  const isAgent = mode === "agent";
  const consumerChips: Array<{ key: "absd" | "hdb" | "trend" | "yield" | "cpf" | "pr"; }> = [
    { key: "absd" }, { key: "hdb" }, { key: "trend" }, { key: "yield" }, { key: "cpf" }, { key: "pr" },
  ];
  // Agent chips lead with workspace-grounded prompts ("today / top leads /
  // overdue") because those are the questions only ProProp can answer —
  // every other generic-AI tool can do listing copy and comps research.
  // The chosen six map 1:1 to the new list_my_* tools the LLM can call.
  const agentChips: Array<{ key: "today" | "topLeads" | "overdue" | "listing" | "comps" | "followup"; }> = [
    { key: "today" }, { key: "topLeads" }, { key: "overdue" },
    { key: "listing" }, { key: "comps" }, { key: "followup" },
  ];

  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.history, chat.liveFlow]);

  useEffect(() => {
    // Lead-capture card is a consumer-side funnel ("a partner agent will call you back").
    // Never show it inside the agent shell — agents are the suppliers, not the audience.
    if (mode === "agent") return;
    if (!chat.sending && chat.assistantReplyCount >= 2 && !localStorage.getItem(LEAD_KEY)) {
      setShowLead(true);
    }
  }, [chat.sending, chat.assistantReplyCount, mode]);

  // B2: ChatWelcomeModal (and any future surface) can seed the composer
  // by dispatching a `chat:seed-input` CustomEvent with `{ text }`. We
  // populate the textarea + auto-resize + focus, but do NOT auto-submit
  // — the user might want to tweak before sending.
  useEffect(() => {
    const onSeed = (e: Event) => {
      const detail = (e as CustomEvent<{ text?: string }>).detail;
      const text = (detail?.text ?? "").toString();
      if (!text) return;
      setInput(text);
      // Defer to next frame so React has committed `value` before resize.
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          autoResize(inputRef.current);
        }
      });
    };
    window.addEventListener("chat:seed-input", onSeed as EventListener);
    return () => window.removeEventListener("chat:seed-input", onSeed as EventListener);
  }, []);

  function autoResize(el: HTMLTextAreaElement) {
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 160) + "px";
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const text = input.trim();
    if (!text || chat.sending) return;
    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    // When the LLM has paused with request_user_input, the composer
    // resolves the pause instead of opening a new user turn — the server
    // is expecting a tool_result, not another user message.
    if (chat.pending?.kind === "hitl") {
      await chat.respond(text);
    } else {
      await chat.send(text);
    }
  }

  async function onApprove() {
    if (chat.sending) return;
    await chat.approve();
  }

  async function onDeny() {
    if (chat.sending) return;
    const reason = denyReason.trim();
    setDenyReason("");
    await chat.deny(reason || undefined);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void onSubmit(e as unknown as React.FormEvent);
    }
  }

  const hasContent = chat.history.some((m) => m.role !== "tool_trace") || chat.liveFlow.length > 0;

  // Show a retry action when the last non-trace message is assistant/gen_ui/error
  // and nothing is currently streaming — lets the user regenerate the reply
  // (which then replaces the stale one instead of appending).
  const lastVisible = [...chat.history].reverse().find((m) => m.role !== "tool_trace");
  const showRetry =
    !chat.sending &&
    chat.liveFlow.length === 0 &&
    chat.canRetry &&
    (lastVisible?.role === "assistant" || lastVisible?.role === "error" || lastVisible?.role === "gen_ui");

  const threadTitle = (() => {
    const first = chat.history.find((m) => m.role === "user");
    if (!first) return "New conversation";
    const words = first.content.trim().replace(/\s+/g, " ");
    return words.length > 52 ? words.slice(0, 50) + "…" : words;
  })();

  return (
    <div className="flex h-full flex-col min-h-0">
      {openProject && (
        <ProjectDetailModal name={openProject} onClose={() => setOpenProject(null)} />
      )}
      {/* Thread header */}
      <div className="hidden shrink-0 items-center gap-3 border-b border-border bg-bg px-5 md:flex" style={{ height: 48 }}>
        <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-text-muted">{t("consumerNav.thread")}</span>
        <span
          className="text-[14px] font-medium text-text"
          style={{ fontFamily: '"Source Serif 4", Georgia, serif', letterSpacing: "-0.01em" }}
        >
          {threadTitle}
        </span>
        <div className="flex-1" />
        <button
          type="button"
          className="btn-ghost hidden text-[11px] sm:flex"
          onClick={() => setHeaderToast(t("consumerNav.shareComingSoon"))}
        >
          {t("consumerNav.share")}
        </button>
        <button
          type="button"
          className="btn-ghost hidden text-[11px] sm:flex"
          onClick={() => setHeaderToast(t("consumerNav.exportComingSoon"))}
        >
          {t("consumerNav.export")}
        </button>
      </div>

      {/* Messages area — generous top padding like legacy (32px) */}
      <div
        ref={messagesRef}
        aria-live="polite"
        className={`scrollbar-thin flex-1 min-h-0 overscroll-y-contain px-4 pb-4 pt-6 sm:px-6 sm:pt-8 ${hasContent ? "overflow-y-auto" : "overflow-hidden"}`}
      >
        <div className={`mx-auto flex w-full max-w-content flex-col gap-1 ${!hasContent ? "h-full" : ""}`}>
          {!hasContent && (
            <EmptyState
              title={t(isAgent ? "agentCopilot.title" : "home.title")}
              subtitle={t(isAgent ? "agentCopilot.subtitle" : "home.subtitle")}
              onChip={(q) => { setInput(q); inputRef.current?.focus(); }}
              chips={isAgent
                ? agentChips.map((c) => ({
                    label: t(`agentCopilot.chips.${c.key}`),
                    q: t(`agentCopilot.suggestionQs.${c.key}`),
                  }))
                : consumerChips.map((c) => ({
                    label: t(`home.chips.${c.key}`),
                    q: t(`home.suggestionQs.${c.key}`),
                  }))}
            />
          )}

          {groupHistory(chat.history).map((group, i) => {
            if (group.kind === "user" || group.kind === "error") {
              return <MessageRow key={i} msg={group.msg} showTrace={showTrace} />;
            }
            if (group.kind === "choice_result") {
              return (
                <HITLChoiceCard
                  key={i}
                  prompt={group.msg.prompt}
                  options={group.msg.options}
                  multiple={false}
                  onSubmit={() => {}}
                  disabled
                  submittedSelected={new Set(group.msg.selected)}
                  submittedOtherText={group.msg.otherText}
                />
              );
            }
            if (group.kind === "form_result") {
              return (
                <HITLFormCard
                  key={i}
                  title={group.msg.title}
                  reason={group.msg.reason}
                  questions={group.msg.questions}
                  onSubmit={() => {}}
                  disabled
                  submittedAnswers={group.msg.answers}
                />
              );
            }
            if (group.kind === "propose_result") {
              return (
                <WorkspaceProposeCard
                  key={i}
                  toolName={group.msg.toolName}
                  args={group.msg.args}
                  onPick={() => {}}
                  disabled
                  resolvedSelected={group.msg.selected}
                  resolvedApplied={group.msg.applied}
                  resolvedPropertyId={group.msg.propertyId}
                  resolvedWorkspaceId={group.msg.destinationWorkspaceId}
                />
              );
            }
            return <AssistantTurnBlock key={i} items={group.items} showTrace={showTrace} conversationId={chat.conversationId} onOpenProject={handleOpenProject} />;
          })}

          {showRetry && (
            <div className="ml-[42px] mt-1 flex">
              <button
                type="button"
                onClick={() => { void chat.retry(); }}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-3 px-2 py-1 text-[12px] text-text-muted transition hover:border-border-strong hover:text-text"
                title={t("common.retry")}
              >
                <RetryIcon size={12} />
                {t("common.retry")}
              </button>
            </div>
          )}

          {chat.pending?.kind === "hitl_choice" ? (
            <HITLChoiceCard
              prompt={chat.pending.prompt || ""}
              options={chat.pending.options ?? []}
              multiple={chat.pending.multiple}
              allowOther={chat.pending.allowOther}
              onSubmit={(selected, otherText) => { void chat.respondChoice(selected, otherText); }}
              disabled={chat.sending}
            />
          ) : null}

          {chat.pending?.kind === "hitl_form" ? (
            <HITLFormCard
              title={chat.pending.formTitle}
              reason={chat.pending.formReason}
              questions={chat.pending.formQuestions ?? []}
              onSubmit={(answers) => { void chat.respondForm(answers); }}
              disabled={chat.sending}
            />
          ) : null}

          {chat.pending?.kind === "workspace_propose" ? (
            <WorkspaceProposeCard
              toolName={chat.pending.toolName || ""}
              args={chat.pending.proposeArgs ?? {}}
              workspaces={chat.workspaces}
              currentWorkspaceId={chat.currentWorkspaceId}
              onPick={(value, wsId) => { void chat.respondChoice([value], undefined, wsId); }}
              disabled={chat.sending}
            />
          ) : null}

          <LiveFlowBlock items={chat.liveFlow} showTrace={showTrace} onOpenProject={handleOpenProject} />

          {showLead && (
            <LeadCaptureCard
              onDismiss={() => setShowLead(false)}
              lastUserMessage={[...chat.history].reverse().find((m) => m.role === "user")?.content}
            />
          )}
        </div>
      </div>

      {/* Input area */}
      <div className="shrink-0 px-4 pt-2 pb-[max(14px,var(--sab))] sm:px-6">
        <form onSubmit={onSubmit} className="mx-auto w-full max-w-content">
          {chat.pending?.kind === "hitl" && (
            <div className="mb-2 flex items-center gap-2 rounded-md border border-accent/30 bg-accent/10 px-2.5 py-1.5 text-[12px] text-accent">
              <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
              {t("home.pendingBadge")}
            </div>
          )}
          {chat.pending?.kind === "approval" ? (
            <ApprovalPanel
              toolName={chat.pending.toolName || "tool"}
              prompt={chat.pending.prompt || t("home.approvalDefaultPrompt", { tool: chat.pending.toolName || "tool" })}
              denyReason={denyReason}
              onDenyReasonChange={setDenyReason}
              onApprove={onApprove}
              onDeny={onDeny}
              disabled={chat.sending}
            />
          ) : chat.pending?.kind === "hitl_choice" ? (
            <div className="chat-input-wrapper" style={{ opacity: 0.5, pointerEvents: "none" }}>
              <textarea rows={1} disabled placeholder="Select an option above to continue…" />
              <button disabled className="chat-send"><SendIcon size={16} /></button>
            </div>
          ) : chat.pending?.kind === "hitl_form" ? (
            <div className="chat-input-wrapper" style={{ opacity: 0.5, pointerEvents: "none" }}>
              <textarea rows={1} disabled placeholder="Answer the questions above to continue…" />
              <button disabled className="chat-send"><SendIcon size={16} /></button>
            </div>
          ) : chat.pending?.kind === "workspace_propose" ? (
            <div className="chat-input-wrapper" style={{ opacity: 0.5, pointerEvents: "none" }}>
              <textarea rows={1} disabled placeholder="Pick an option above to continue…" />
              <button disabled className="chat-send"><SendIcon size={16} /></button>
            </div>
          ) : (
            <div className="chat-input-wrapper">
              <textarea
                ref={inputRef}
                rows={1}
                placeholder={chat.pending?.kind === "hitl"
                  ? t("home.pendingPlaceholder")
                  : t(isAgent ? "agentCopilot.placeholder" : "home.placeholder")}
                value={input}
                onChange={(e) => { setInput(e.target.value); autoResize(e.currentTarget); }}
                onKeyDown={onKeyDown}
                required
              />
              {chat.sending ? (
                <button
                  type="button"
                  aria-label="Stop generating"
                  onClick={chat.stop}
                  className="chat-send"
                >
                  <StopIcon size={14} />
                </button>
              ) : (
                <button
                  type="submit"
                  aria-label="Send"
                  disabled={!input.trim()}
                  className="chat-send"
                >
                  <SendIcon size={16} />
                </button>
              )}
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 px-1 text-[11px] text-text-dim">
            <span>{t("app.disclaimer")}</span>
            <div className="flex items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-1.5 hover:text-text-sec">
                <input
                  type="checkbox"
                  checked={showTrace}
                  onChange={(e) => setShowTrace(e.target.checked)}
                  className="h-3 w-3 accent-accent"
                />
                {t("home.showTrace")}
              </label>
              <button
                type="button"
                className="hover:text-text-sec"
                onClick={chat.clear}
              >
                {t("home.clear")}
              </button>
              <button
                type="button"
                title={t("home.new")}
                className="inline-flex h-6 w-6 items-center justify-center rounded border border-border text-text-muted transition hover:border-border-strong hover:text-text"
                onClick={chat.clear}
              >
                <PlusIcon size={12} />
              </button>
            </div>
          </div>
        </form>
      </div>
      {headerToast && <Toast message={headerToast} onDismiss={() => setHeaderToast(null)} />}
    </div>
  );
}

function EmptyState({
  title, subtitle, onChip, chips,
}: {
  title: string;
  subtitle: string;
  onChip: (q: string) => void;
  chips: Array<{ label: string; q: string }>;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2.5 px-6 py-6 text-center">
      <div className="empty-icon-glow mb-2">
        <svg width={58} height={58} viewBox="0 0 58 58" fill="none">
          <rect width="58" height="58" rx="14" style={{ fill: "var(--ai-bg)", stroke: "var(--ai-rule)" }} strokeWidth="1" />
          <path d="M22 14 H12 a4 4 0 0 0 -4 4 V40 a4 4 0 0 0 4 4 H22"
            fill="none" strokeWidth="4.2" strokeLinecap="round" style={{ stroke: "var(--color-text)" }} />
          <path d="M36 14 H46 a4 4 0 0 1 4 4 V40 a4 4 0 0 1 -4 4 H36"
            fill="none" strokeWidth="4.2" strokeLinecap="round" style={{ stroke: "var(--accent)" }} />
          <circle cx="29" cy="29" r="3" style={{ fill: "var(--ai-accent)" }} />
        </svg>
      </div>
      <h2
        className="text-[22px] leading-tight tracking-tight text-text sm:text-[26px]"
        style={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 500 }}
      >
        {title}
      </h2>
      <p className="max-w-md text-[14px] leading-[1.6] text-text-sec">{subtitle}</p>
      <div className="mt-3 flex max-w-[560px] flex-wrap justify-center gap-2">
        {chips.map((c) => (
          <button
            key={c.label}
            type="button"
            onClick={() => onChip(c.q)}
            className="chip"
          >
            {c.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ToolCallBlock({ name }: { name: string }) {
  const { t } = useTranslation();
  if (HITL_TOOL_NAMES.has(name)) return null;
  const label = t(`tools.${name}`, { defaultValue: name });
  return (
    <div className="my-0.5 flex items-center gap-2 text-[11.5px] text-accent/60">
      <svg width={12} height={12} viewBox="0 0 16 16" fill="none" className="shrink-0">
        <path d="M8 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" stroke="currentColor" strokeWidth="1.4" />
        <path
          d="M8 2v1.5M8 12.5V14M2 8h1.5M12.5 8H14M3.75 3.75l1.06 1.06M11.19 11.19l1.06 1.06M3.75 12.25l1.06-1.06M11.19 4.81l1.06-1.06"
          stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"
        />
      </svg>
      <span>{label}</span>
    </div>
  );
}

function MessageRow({ msg, showTrace, streaming }: { msg: ChatMessage; showTrace: boolean; streaming?: boolean }) {
  if (msg.role === "tool_trace") {
    if (!showTrace) return null;
    return <ToolCallBlock name={msg.name} />;
  }

  const isUser = msg.role === "user";
  const isError = msg.role === "error";

  return (
    <div className={`msg-in flex gap-3 py-1 ${isUser ? "flex-row-reverse" : ""}`}>
      <Avatar role={isUser ? "user" : "assistant"} />

      {isUser ? (
        <div className="user-bubble max-w-[82%] rounded-[14px] rounded-tr-md border px-3.5 py-2.5 text-[14px] leading-[1.6]">
          <span className="whitespace-pre-wrap break-words">{msg.content}</span>
        </div>
      ) : isError ? (
        <div className="max-w-[90%] rounded-lg border border-bad/25 bg-bad/[0.06] px-3 py-2 text-[13px] text-bad">
          {(msg as { content: string }).content}
        </div>
      ) : (
        <div className="ai-card markdown max-w-[90%] px-0 py-1 text-[14px] leading-[1.6] text-text">
          <span dangerouslySetInnerHTML={{ __html: renderMarkdown((msg as { content: string }).content) }} />
          {streaming && <span className="stream-cursor" />}
        </div>
      )}
    </div>
  );
}

function Avatar({ role }: { role: "user" | "assistant" }) {
  if (role === "user") {
    return (
      <div
        className="user-avatar flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-md text-[12px] font-bold"
        style={{ marginTop: 3, letterSpacing: "-0.3px" }}
      >
        U
      </div>
    );
  }
  return (
    <div
      className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border"
      style={{
        background: "var(--ai-bg)",
        borderColor: "var(--ai-rule)",
        marginTop: 3,
      }}
    >
      <AISparkleIcon size={14} />
    </div>
  );
}

function ThinkingRow() {
  const { t } = useTranslation();
  // The LLM can pause 3–6s before the first token on long-form Chinese
  // prompts. Three bouncing dots alone read as "frozen" — show a subtle
  // status label after a short delay so the user knows it's still working.
  const [showLabel, setShowLabel] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setShowLabel(true), 1500);
    return () => window.clearTimeout(id);
  }, []);
  return (
    <div className="msg-in flex items-center gap-3 py-1">
      <Avatar role="assistant" />
      <div className="flex items-center gap-2">
        <div className="thinking-dots">
          <span /><span /><span />
        </div>
        {showLabel && (
          <span className="text-[11.5px] text-text-muted">
            {t("home.thinking")}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Feedback helpers ────────────────────────────────────────────────────────

type FeedbackStatus = "liked" | "unliked" | null;

function Toast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 2500);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div
      className="fixed top-4 left-1/2 z-[9999] flex -translate-x-1/2 items-center gap-2 rounded-full border border-border bg-surface px-4 py-2 text-[13px] font-medium text-text shadow-lg animate-slide-in"
      style={{ pointerEvents: "none" }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="shrink-0 text-good">
        <path d="M2.5 7l3 3 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {message}
    </div>
  );
}

function FeedbackDrawer({ onClose, onSubmit }: {
  onClose: () => void;
  onSubmit: (text: string) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9990] bg-black/40 animate-fade-in"
        onClick={onClose}
      />
      {/* Bottom sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[9991] rounded-t-xl border-t border-border bg-surface p-5 shadow-xl animate-slide-in"
      >
        <div className="mx-auto max-w-lg">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[14px] font-semibold text-text">
              {t("feedback.drawerTitle", { defaultValue: "How can we improve this response?" })}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-text-muted hover:text-text transition"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <textarea
            ref={textareaRef}
            rows={3}
            className="input-field mb-3 w-full resize-none"
            placeholder={t("feedback.placeholder", { defaultValue: "Optional — tell us what was wrong…" })}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onSubmit(text)}
              className="btn-primary px-4 py-1.5 text-[13px]"
            >
              {t("feedback.submit", { defaultValue: "Submit" })}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost px-4 py-1.5 text-[13px]"
            >
              {t("feedback.skip", { defaultValue: "Skip" })}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function FeedbackBar({ blockId, conversationId, textContent }: {
  blockId: string | undefined;
  conversationId: string | null;
  textContent: string;
}) {
  const [status, setStatus] = useState<FeedbackStatus>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showDrawer, setShowDrawer] = useState(false);
  // Capture mount time once so re-renders from toast/drawer state don't shift the clock
  const displayTime = useMemo(
    () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }),
    [],
  );

  const dismissToast = useCallback(() => setToast(null), []);

  const submitFeedback = useCallback((s: FeedbackStatus, text?: string) => {
    if (!conversationId || !blockId) return;
    const body: Record<string, unknown> = { block_id: blockId, status: s };
    if (text !== undefined) body.text = text || null;
    void apiFetch(`/conversations/${encodeURIComponent(conversationId)}/feedback`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).catch(() => { /* silent */ });
  }, [blockId, conversationId]);

  function handleLike() {
    const next: FeedbackStatus = status === "liked" ? null : "liked";
    setStatus(next);
    submitFeedback(next);
    if (next === "liked") setToast("Thanks for your feedback!");
  }

  function handleUnlike() {
    if (status === "unliked") {
      setStatus(null);
      submitFeedback(null);
      return;
    }
    setStatus("unliked");
    setShowDrawer(true);
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(textContent);
      setToast("Copied to clipboard");
    } catch {
      const el = document.createElement("textarea");
      el.value = textContent;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setToast("Copied to clipboard");
    }
  }

  function handleDrawerSubmit(feedbackText: string) {
    setShowDrawer(false);
    submitFeedback("unliked", feedbackText);
  }

  function handleDrawerClose() {
    setShowDrawer(false);
    submitFeedback("unliked");
  }

  return (
    <>
      {/* Row sits inside the ai-card, separated by a dashed rule */}
      <div
        className="mt-3 flex items-center gap-0.5 border-t border-dashed pt-2"
        style={{ borderColor: "var(--ai-rule)" }}
      >
        <button
          type="button"
          title="Helpful"
          onClick={handleLike}
          className={`flex h-6 w-6 items-center justify-center rounded border transition ${
            status === "liked"
              ? "border-good/40 bg-good/10 text-good"
              : "border-transparent text-text-dim hover:border-border hover:text-text-muted"
          }`}
        >
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M4 6.5L7 2c.8 0 1.5.7 1.5 1.5V6h3.5c.5 0 1 .5 1 1L12 12H4m0-5.5V12M4 6.5H2v5.5h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          type="button"
          title="Not helpful"
          onClick={handleUnlike}
          className={`flex h-6 w-6 items-center justify-center rounded border transition ${
            status === "unliked"
              ? "border-bad/40 bg-bad/10 text-bad"
              : "border-transparent text-text-dim hover:border-border hover:text-text-muted"
          }`}
        >
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" style={{ transform: "rotate(180deg)" }}>
            <path d="M4 6.5L7 2c.8 0 1.5.7 1.5 1.5V6h3.5c.5 0 1 .5 1 1L12 12H4m0-5.5V12M4 6.5H2v5.5h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          type="button"
          title="Copy"
          onClick={() => { void handleCopy(); }}
          className="flex h-6 w-6 items-center justify-center rounded border border-transparent text-text-dim transition hover:border-border hover:text-text-muted"
        >
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <rect x="4" y="2" width="8" height="10" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
            <path d="M4 4H3a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        </button>
        <div className="flex-1" />
        <span className="text-[10px] tabular-nums" style={{ color: "var(--ai-rule)", fontFamily: "var(--font-ui)" }}>
          {displayTime}
        </span>
      </div>
      {toast && <Toast message={toast} onDismiss={dismissToast} />}
      {showDrawer && <FeedbackDrawer onClose={handleDrawerClose} onSubmit={handleDrawerSubmit} />}
    </>
  );
}

// ─── End feedback helpers ─────────────────────────────────────────────────────

function AssistantTurnBlock({ items, showTrace, conversationId, onOpenProject }: {
  items: AssistantTurnItem[];
  showTrace: boolean;
  conversationId: string | null;
  onOpenProject: (name: string) => void;
}) {
  const textItems = items.filter((item): item is { type: "text"; content: string; id?: string } => item.type === "text");
  const blockId = textItems.find((item) => item.id)?.id;
  const textContent = textItems.map((item) => item.content).join("\n\n");

  // Aggregate every project name surfaced by tool calls in this turn so
  // the chip row appears once below the last assistant bubble (rather
  // than per-trace, which would visually clutter for multi-tool turns).
  const turnProjects = dedupeProjects(
    ...items.flatMap((item) => (item.type === "trace" && item.projects ? [item.projects] : [])),
  );
  // Same aggregation for web_search references — sources used to ground
  // the answer. Rendered as a second chip row directly below the
  // project chips so the assistant turn carries a coherent "things we
  // looked at" footer.
  const turnRefs = dedupeReviewRefs(
    ...items.flatMap((item) => (item.type === "trace" && item.refs ? [item.refs] : [])),
  );

  // Index of the last text item — FeedbackBar is rendered inside that card
  let lastTextIdx = -1;
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i].type === "text") { lastTextIdx = i; break; }
  }

  return (
    <div className="msg-in flex gap-3 py-1">
      <Avatar role="assistant" />
      <div className="min-w-0 flex-1 flex flex-col">
        {/* AI name label */}
        <div className="mb-1.5 flex items-center gap-1.5" style={{ color: "var(--ai-accent)" }}>
          <span className="text-[11px] font-semibold" style={{ letterSpacing: "0.02em" }}>ProProp</span>
          <span
            className="rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-[0.1em]"
            style={{ background: "var(--ai-bg)", color: "var(--ai-accent)", border: "1px solid var(--ai-rule)" }}
          >
            AI
          </span>
        </div>
        {items.map((item, i) => {
          if (item.type === "trace") {
            if (!showTrace) return null;
            return <ToolCallBlock key={i} name={item.name} />;
          }
          if (item.type === "gen_ui") {
            return <GenUIBlock key={i} payload={item.payload} />;
          }
          const isLastText = i === lastTextIdx;
          return (
            <div key={i} className="ai-card markdown py-0.5 text-[14px] leading-[1.6] text-text">
              <span dangerouslySetInnerHTML={{ __html: renderMarkdown(item.content) }} />
              {isLastText && turnProjects.length > 0 && (
                <ProjectChipRow projects={turnProjects} onOpen={onOpenProject} />
              )}
              {isLastText && turnRefs.length > 0 && (
                <ReviewChipRow refs={turnRefs} />
              )}
              {isLastText && textContent && (
                <FeedbackBar blockId={blockId} conversationId={conversationId} textContent={textContent} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LiveFlowBlock({ items, showTrace, onOpenProject }: {
  items: LiveFlowItem[];
  showTrace: boolean;
  onOpenProject: (name: string) => void;
}) {
  if (items.length === 0) return null;
  if (items.length === 1 && items[0].kind === "thinking") return <ThinkingRow />;
  // Aggregate projects from every tool item so far this turn — same
  // grouping as AssistantTurnBlock, but live.
  const liveProjects = dedupeProjects(
    ...items.flatMap((item) => (item.kind === "tool" && item.projects ? [item.projects] : [])),
  );
  const liveRefs = dedupeReviewRefs(
    ...items.flatMap((item) => (item.kind === "tool" && item.refs ? [item.refs] : [])),
  );
  let lastTextIdx = -1;
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i].kind === "text") { lastTextIdx = i; break; }
  }
  return (
    <div className="msg-in flex gap-3 py-1">
      <Avatar role="assistant" />
      <div className="min-w-0 flex-1 flex flex-col pt-1">
        {items.map((item, i) => {
          if (item.kind === "thinking") return null;
          if (item.kind === "active") return <ActiveToolRow key={i} name={item.name} />;
          if (item.kind === "tool") {
            if (!showTrace) return null;
            return <ToolCallBlock key={i} name={item.name} />;
          }
          if (item.kind === "gen_ui") {
            return <GenUIBlock key={i} payload={item.payload} />;
          }
          const isLast = i === lastTextIdx;
          return (
            <div key={i} className="ai-card markdown py-0.5 text-[14px] leading-[1.6] text-text">
              <span dangerouslySetInnerHTML={{ __html: renderMarkdown(item.content) }} />
              {isLast && <span className="stream-cursor" />}
              {isLast && liveProjects.length > 0 && (
                <ProjectChipRow projects={liveProjects} onOpen={onOpenProject} />
              )}
              {isLast && liveRefs.length > 0 && (
                <ReviewChipRow refs={liveRefs} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActiveToolRow({ name }: { name: string }) {
  const { t } = useTranslation();
  const label = t(`tools.${name}`, { defaultValue: name });
  return (
    <div className="flex items-center gap-2 py-0.5 font-mono text-[11.5px] text-good/80">
      <svg width={12} height={12} viewBox="0 0 10 10" fill="none" className="animate-spin-slow">
        <circle cx="5" cy="5" r="4" stroke="#a5d16e" strokeWidth="1.2" strokeDasharray="6 6" />
      </svg>
      {t("home.callingTool", { tool: label })}
    </div>
  );
}

function ApprovalPanel({
  toolName, prompt, denyReason, onDenyReasonChange, onApprove, onDeny, disabled,
}: {
  toolName: string;
  prompt: string;
  denyReason: string;
  onDenyReasonChange: (v: string) => void;
  onApprove: () => void | Promise<void>;
  onDeny: () => void | Promise<void>;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="mb-2 rounded-lg border border-warn/40 bg-warn/[0.06] p-3">
      <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold text-warn">
        <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-warn" />
        {t("home.approvalBadge", { tool: toolName })}
      </div>
      <div className="mb-3 whitespace-pre-wrap break-words text-[13px] leading-[1.55] text-text">
        {prompt}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          type="text"
          className="input-field flex-1"
          placeholder={t("home.denyReasonPlaceholder")}
          value={denyReason}
          onChange={(e) => onDenyReasonChange(e.target.value)}
          disabled={disabled}
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => { void onDeny(); }}
            disabled={disabled}
            className="rounded-md border border-bad/35 bg-bad/10 px-3 py-1.5 text-[13px] font-medium text-bad transition hover:border-bad/60 hover:bg-bad/15 disabled:opacity-50"
          >
            {t("home.deny")}
          </button>
          <button
            type="button"
            onClick={() => { void onApprove(); }}
            disabled={disabled}
            className="rounded-md border border-good/40 bg-good/15 px-3 py-1.5 text-[13px] font-medium text-good transition hover:border-good/60 hover:bg-good/20 disabled:opacity-50"
          >
            {t("home.approve")}
          </button>
        </div>
      </div>
    </div>
  );
}

function HITLChoiceCard({
  prompt, options, multiple, allowOther, onSubmit, disabled, submittedSelected, submittedOtherText,
}: {
  prompt?: string;
  options: ChoiceOption[];
  multiple?: boolean;
  allowOther?: boolean;
  onSubmit: (selected: string[], otherText?: string) => void;
  disabled: boolean;
  submittedSelected?: Set<string>;
  submittedOtherText?: string;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [otherText, setOtherText] = useState("");

  // submittedSelected freezes the card — use it as source of truth when set
  const effectiveSelected = submittedSelected ?? selected;
  const effectiveOther = submittedOtherText ?? otherText;
  const isLocked = !!submittedSelected || disabled;
  const otherSelected = effectiveSelected.has(CHOICE_OTHER_VALUE);

  function toggle(value: string) {
    if (isLocked) return;
    if (!multiple) {
      // Single-select with "Other": don't auto-submit — wait for the user to
      // type their value and click confirm. Other choices auto-submit immediately.
      if (value === CHOICE_OTHER_VALUE) {
        setSelected(new Set([CHOICE_OTHER_VALUE]));
        return;
      }
      onSubmit([value]);
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }

  // Confirm gate: visible whenever we need an explicit click rather than auto-
  // submit on chip tap (multi-select, or single-select with "Other" chosen).
  const needsConfirm = !submittedSelected && (
    (multiple && effectiveSelected.size > 0) ||
    (!multiple && otherSelected)
  );
  const otherTextRequired = otherSelected;
  const otherTextValid = !otherTextRequired || otherText.trim().length > 0;

  return (
    <div className="msg-in flex gap-3 py-1">
      <Avatar role="assistant" />
      <div className="min-w-0 flex-1 flex flex-col">
        {/* Label row */}
        <div className="mb-1.5 flex items-center gap-1.5" style={{ color: "var(--ai-accent)" }}>
          <span className="text-[11px] font-semibold tracking-[0.02em]">Proactive · ProProp</span>
          <span
            className="rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-[0.1em]"
            style={{ background: "var(--ai-bg)", color: "var(--ai-accent)", border: "1px solid var(--ai-rule)" }}
          >
            AI
          </span>
        </div>
        {/* Proactive card — dashed border, no fill */}
        <div
          className="flex flex-col gap-3"
          style={{ border: "1px dashed var(--ai-rule)", borderRadius: 10, padding: "12px 14px" }}
        >
          {prompt && (
            <p
              className="text-[14px] leading-[1.6] text-text-sec"
              style={{ fontFamily: '"Source Serif 4", Georgia, serif', fontStyle: "italic" }}
            >
              {prompt}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {options.map((opt) => {
              const isActive = effectiveSelected.has(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={isLocked}
                  onClick={() => toggle(opt.value)}
                  className={chipClassName(isActive, isLocked, !!multiple)}
                >
                  {opt.label}
                </button>
              );
            })}
            {allowOther && (
              <button
                key={CHOICE_OTHER_VALUE}
                type="button"
                disabled={isLocked}
                onClick={() => toggle(CHOICE_OTHER_VALUE)}
                className={chipClassName(otherSelected, isLocked, !!multiple)}
              >
                {t("home.otherOption")}
              </button>
            )}
          </div>
          {otherSelected && (
            <input
              type="text"
              value={effectiveOther}
              onChange={(e) => setOtherText(e.target.value)}
              disabled={isLocked}
              placeholder={t("home.otherPlaceholder")}
              className="input-field text-[13px]"
            />
          )}
          {needsConfirm && (
            <button
              type="button"
              disabled={isLocked || !otherTextValid}
              onClick={() => {
                const sel = [...effectiveSelected];
                onSubmit(sel, otherSelected ? otherText.trim() : undefined);
              }}
              className="btn-primary self-start text-[13px]"
            >
              {t("home.confirmSelection")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// chipClassName styles a choice chip uniformly across single- and multi-
// select. Inactive is neutral (so unanswered questions don't read as
// pre-selected), active is a solid accent fill (clear "you picked this"),
// and locked dims to gray. The `multiple` arg is unused but kept so call
// sites can stay symmetric with single/multi semantics.
function chipClassName(isActive: boolean, isLocked: boolean, _multiple: boolean): string {
  const base = "rounded-lg border px-3.5 py-1.5 text-[13px] font-medium transition ";
  if (isActive) return base + "border-accent bg-accent text-white shadow-sm";
  if (isLocked) return base + "border-border bg-surface-3 text-text-dim opacity-50 cursor-not-allowed";
  return base + "border-border bg-surface-2 text-text-sec hover:border-accent/60 hover:bg-surface-3 hover:text-text cursor-pointer";
}

// HITLFormCard renders a multi-question request_user_form pause. Each
// question is one of: free-text, single_choice, multi_choice. Choice
// questions support an "Other" escape hatch (allow_other) that reveals an
// inline text input. The card collects all answers locally and submits in
// one shot when every required question has a non-empty answer.
function HITLFormCard({
  title, reason, questions, onSubmit, disabled, submittedAnswers,
}: {
  title?: string;
  reason?: string;
  questions: FormQuestion[];
  onSubmit: (answers: FormAnswers) => void;
  disabled: boolean;
  submittedAnswers?: FormAnswers;
}) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<FormAnswers>({});
  const isLocked = !!submittedAnswers || disabled;
  const effective: FormAnswers = submittedAnswers ?? answers;

  function setAnswer(qid: string, a: FormAnswer) {
    setAnswers((prev) => ({ ...prev, [qid]: a }));
  }

  function answerValid(q: FormQuestion): boolean {
    const required = q.required !== false;
    const a = effective[q.id];
    if (q.type === "text") {
      const txt = ((a as FormAnswerText | undefined)?.text ?? "").trim();
      return required ? txt.length > 0 : true;
    }
    if (q.type === "single_choice") {
      const sa = a as FormAnswerSingle | undefined;
      const v = sa?.value;
      if (!v) return !required;
      if (v === CHOICE_OTHER_VALUE) return (sa?.other_text ?? "").trim().length > 0;
      return true;
    }
    // multi_choice
    const ma = a as FormAnswerMulti | undefined;
    const vs = ma?.values ?? [];
    if (vs.length === 0) return !required;
    if (vs.includes(CHOICE_OTHER_VALUE)) return (ma?.other_text ?? "").trim().length > 0;
    return true;
  }

  // Belt-and-suspenders: useChat.ts coerces questions via Array.isArray
  // when hydrating history, but a defensive check here keeps a stray
  // malformed shape from blowing up the entire conversation render —
  // this exact site produced "n.every is not a function" in prod on
  // /chat/:id for a historical form_result whose questions field
  // wasn't an array.
  const safeQuestions = Array.isArray(questions) ? questions : [];
  const allValid = safeQuestions.every(answerValid);

  return (
    <div className="msg-in flex gap-3 py-1">
      <Avatar role="assistant" />
      <div className="min-w-0 flex-1 flex flex-col">
        <div className="mb-1.5 flex items-center gap-1.5" style={{ color: "var(--ai-accent)" }}>
          <span className="text-[11px] font-semibold tracking-[0.02em]">Proactive · ProProp</span>
          <span
            className="rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-[0.1em]"
            style={{ background: "var(--ai-bg)", color: "var(--ai-accent)", border: "1px solid var(--ai-rule)" }}
          >
            AI
          </span>
        </div>
        <div
          className="flex flex-col gap-4"
          style={{ border: "1px dashed var(--ai-rule)", borderRadius: 10, padding: "12px 14px" }}
        >
          {(title || reason) && (
            <div className="flex flex-col gap-1">
              {title && (
                <p
                  className="text-[14px] font-semibold text-text"
                  style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
                >
                  {title}
                </p>
              )}
              {reason && (
                <p
                  className="text-[12.5px] leading-[1.55] text-text-sec"
                  style={{ fontFamily: '"Source Serif 4", Georgia, serif', fontStyle: "italic" }}
                >
                  {reason}
                </p>
              )}
            </div>
          )}
          {safeQuestions.map((q) => (
            <FormQuestionRow
              key={q.id}
              q={q}
              answer={effective[q.id]}
              disabled={isLocked}
              onChange={(a) => setAnswer(q.id, a)}
            />
          ))}
          {!submittedAnswers && (
            <button
              type="button"
              disabled={isLocked || !allValid}
              onClick={() => onSubmit(effective)}
              className="btn-primary self-start text-[13px]"
            >
              {t("home.submitForm")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FormQuestionRow({
  q, answer, disabled, onChange,
}: {
  q: FormQuestion;
  answer: FormAnswer | undefined;
  disabled: boolean;
  onChange: (a: FormAnswer) => void;
}) {
  const { t } = useTranslation();
  const required = q.required !== false;

  if (q.type === "text") {
    const text = (answer as FormAnswerText | undefined)?.text ?? "";
    return (
      <div className="flex flex-col gap-1.5">
        <label className="text-[13px] font-medium text-text">
          {q.prompt}
          {!required && <span className="ml-1 text-[11px] text-text-muted">({t("home.optional")})</span>}
        </label>
        <input
          type="text"
          value={text}
          disabled={disabled}
          placeholder={q.placeholder}
          onChange={(e) => onChange({ text: e.target.value })}
          className="input-field text-[13px]"
        />
      </div>
    );
  }

  if (q.type === "single_choice") {
    const sa = (answer as FormAnswerSingle | undefined) ?? { value: "" };
    const otherSelected = sa.value === CHOICE_OTHER_VALUE;
    return (
      <div className="flex flex-col gap-1.5">
        <label className="text-[13px] font-medium text-text">
          {q.prompt}
          {!required && <span className="ml-1 text-[11px] text-text-muted">({t("home.optional")})</span>}
        </label>
        <div className="flex flex-wrap gap-2">
          {(q.options ?? []).map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange({ value: opt.value })}
              className={chipClassName(sa.value === opt.value, disabled, false)}
            >
              {opt.label}
            </button>
          ))}
          {q.allow_other && (
            <button
              key={CHOICE_OTHER_VALUE}
              type="button"
              disabled={disabled}
              onClick={() => onChange({ value: CHOICE_OTHER_VALUE, other_text: sa.other_text ?? "" })}
              className={chipClassName(otherSelected, disabled, false)}
            >
              {t("home.otherOption")}
            </button>
          )}
        </div>
        {otherSelected && (
          <input
            type="text"
            value={sa.other_text ?? ""}
            disabled={disabled}
            placeholder={t("home.otherPlaceholder")}
            onChange={(e) => onChange({ value: CHOICE_OTHER_VALUE, other_text: e.target.value })}
            className="input-field text-[13px]"
          />
        )}
      </div>
    );
  }

  // multi_choice
  const ma = (answer as FormAnswerMulti | undefined) ?? { values: [] };
  const values = ma.values ?? [];
  const otherSelected = values.includes(CHOICE_OTHER_VALUE);
  function toggleMulti(v: string) {
    const has = values.includes(v);
    const next = has ? values.filter((x) => x !== v) : [...values, v];
    onChange({ values: next, other_text: ma.other_text });
  }
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[13px] font-medium text-text">
        {q.prompt}
        {!required && <span className="ml-1 text-[11px] text-text-muted">({t("home.optional")})</span>}
      </label>
      <div className="flex flex-wrap gap-2">
        {(q.options ?? []).map((opt) => (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => toggleMulti(opt.value)}
            className={chipClassName(values.includes(opt.value), disabled, true)}
          >
            {opt.label}
          </button>
        ))}
        {q.allow_other && (
          <button
            key={CHOICE_OTHER_VALUE}
            type="button"
            disabled={disabled}
            onClick={() => toggleMulti(CHOICE_OTHER_VALUE)}
            className={chipClassName(otherSelected, disabled, true)}
          >
            {t("home.otherOption")}
          </button>
        )}
      </div>
      {otherSelected && (
        <input
          type="text"
          value={ma.other_text ?? ""}
          disabled={disabled}
          placeholder={t("home.otherPlaceholder")}
          onChange={(e) => onChange({ values, other_text: e.target.value })}
          className="input-field text-[13px]"
        />
      )}
    </div>
  );
}

function LeadCaptureCard({
  onDismiss, lastUserMessage,
}: { onDismiss: () => void; lastUserMessage?: string }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!name.trim() || !phone.trim()) return;
    setSending(true);
    setErr(null);
    const context = (lastUserMessage || "Singapore property enquiry").slice(0, 300);
    try {
      await apiPostJson("/leads/capture", { name, phone, source: "ai-chat", context });
      // Only flag "submitted" and show success when the POST actually
      // succeeded. The previous catch{} swallowed every failure mode
      // (DB down, 500, network drop) so the user saw "✅ 已收到!" while
      // the lead never reached chat_leads — and agents wondered why
      // it never appeared in their dashboard.
      localStorage.setItem(LEAD_KEY, "1");
      setSuccess(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="msg-in relative my-3 rounded-xl border border-accent/25 p-4 pr-10 sm:p-5"
      style={{
        background: "linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(139,92,246,0.06) 100%)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
    >
      <button
        aria-label="Dismiss"
        onClick={onDismiss}
        className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-md border border-border text-text-muted hover:text-text"
      >
        ×
      </button>
      <div className="flex gap-3">
        <div className="text-xl">💡</div>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-bold text-text">{t("home.lead.title")}</div>
          <div className="mt-1 text-[12.5px] text-text-sec">{t("home.lead.sub")}</div>
          {success ? (
            <div className="mt-3 text-[13px] font-medium text-good">{t("home.lead.success")}</div>
          ) : (
            <div className="mt-3 flex flex-col gap-2">
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="text"
                  className="input-field flex-1"
                  placeholder={t("home.lead.name")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <input
                  type="text"
                  className="input-field flex-1"
                  placeholder={t("home.lead.phone")}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              {err && (
                <div className="text-[12px]" style={{ color: "var(--neg)" }}>
                  {err}
                </div>
              )}
              <button
                type="button"
                className="btn-primary w-full self-start sm:w-auto"
                onClick={submit}
                disabled={sending}
              >
                {sending ? t("home.lead.sending") : t("home.lead.submit")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// WorkspaceProposeCard renders a HITL chip for the four chat-side
// workspace mutation proposals (Ship 2). It dispatches the user's
// choice value back through respondChoice, which the backend's
// PauseKindWorkspacePropose branch routes to the right workspace
// mutation by tool_name.
//
// Copy is built from the propose tool's args (parsed tool_input).
// Buttons are tool-specific:
//   shortlist         → [Add] [Skip]
//   intent_update     → [Update workspace] [Memory-only] [Ignore]
//   preference_sync   → [Sync] [Skip]
//   dossier_note      → [Add] [Skip]
function WorkspaceProposeCard({
  toolName, args, onPick, disabled, resolvedSelected, resolvedApplied,
  resolvedPropertyId, resolvedWorkspaceId,
  workspaces, currentWorkspaceId,
}: {
  toolName: string;
  args: Record<string, unknown>;
  // onPick fires when the user clicks one of the action buttons. The
  // second arg is the destination workspace id the user wants the
  // mutation to land in — passed through to /chat/respond, validated
  // BE-side against the conversation's user. Omitted/empty falls back
  // to the conversation's linked workspace.
  onPick: (value: string, workspaceId?: string) => void;
  disabled: boolean;
  // When set the card renders in frozen "we did X" mode — the buttons
  // are replaced by a status badge derived from resolvedApplied and the
  // user's choice from resolvedSelected. Mirrors how HITLChoiceCard's
  // `submittedSelected` prop freezes a resolved turn.
  resolvedSelected?: string;
  resolvedApplied?: string;
  // resolvedPropertyId + resolvedWorkspaceId let the frozen card show
  // a deep-link into the affected workspace surface (dossier for
  // shortlist/dossier_note, workspace root for brief edits). Both are
  // populated post-hydration; the live commit lacks them but the chip
  // re-renders once /messages refresh fills them in.
  resolvedPropertyId?: string;
  resolvedWorkspaceId?: string;
  // workspaces is the user's active workspace list (sorted by
  // updated_at DESC). Empty when the lazy fetch hasn't completed yet
  // or the user is anonymous — the card degrades gracefully (no name
  // shown, no picker).
  workspaces?: { id: string; name: string }[];
  currentWorkspaceId?: string;
}) {
  const str = (k: string) => {
    const v = args[k];
    return typeof v === "string" ? v : "";
  };
  let title = "Suggestion";
  let body: React.ReactNode = null;
  let buttons: Array<{ label: string; value: string; primary?: boolean }> = [];
  switch (toolName) {
    case "workspace_propose_shortlist": {
      // The propose tool only carries the project name + reason. District,
      // tenure, TOP year, MRT etc. are filled by the server from the
      // canonical project database on Add — see enrichShortlistFromDataEngine
      // in httpapi/server.go. We deliberately don't try to surface those
      // fields on the chip; the user sees them on the dossier after Add.
      const project = str("project_name");
      const reason = str("reason");
      title = "Add to your worklist?";
      body = (
        <>
          <div><strong>{project}</strong></div>
          {reason ? <div className="mt-1 text-text-muted">{reason}</div> : null}
        </>
      );
      buttons = [
        { label: "Add", value: "add", primary: true },
        { label: "Skip", value: "skip" },
      ];
      break;
    }
    case "workspace_propose_intent_update": {
      const field = str("field");
      const current = str("current_value");
      const proposed = str("proposed_value");
      const reason = str("reason");
      title = "Update workspace brief?";
      body = (
        <>
          <div>
            <strong>{field}</strong>
            {current ? <>: <span className="text-text-muted">{current}</span> →</> : ":"}
            {" "}
            <strong>{proposed}</strong>
          </div>
          {reason ? <div className="mt-1 text-text-muted">{reason}</div> : null}
        </>
      );
      buttons = [
        { label: "Update workspace", value: "update_workspace", primary: true },
        { label: "Memory only", value: "memory_only" },
        { label: "Ignore", value: "ignore" },
      ];
      break;
    }
    case "workspace_propose_preference_sync": {
      const label = str("priority_label");
      const reason = str("reason");
      title = "Sync priority to workspace?";
      body = (
        <>
          <div><strong>{label}</strong></div>
          {reason ? <div className="mt-1 text-text-muted">{reason}</div> : null}
        </>
      );
      buttons = [
        { label: "Sync", value: "sync", primary: true },
        { label: "Skip", value: "skip" },
      ];
      break;
    }
    case "workspace_propose_dossier_note": {
      const kind = str("kind");
      const text = str("text");
      title = `Add to dossier (${kind})?`;
      body = (
        <>
          <div>{text}</div>
        </>
      );
      buttons = [
        { label: "Add", value: "add", primary: true },
        { label: "Skip", value: "skip" },
      ];
      break;
    }
    default:
      title = "Suggestion";
      body = <div>{toolName}</div>;
      buttons = [{ label: "OK", value: "ok", primary: true }];
  }
  // Destination picker state. Only relevant in live mode. Initial
  // value comes from the conversation's linked workspace; the user can
  // override via the picker when they have more than one workspace.
  const [chosenWsId, setChosenWsId] = useState<string>("");
  useEffect(() => {
    if (currentWorkspaceId) setChosenWsId(currentWorkspaceId);
  }, [currentWorkspaceId]);
  const wsList = workspaces ?? [];
  const effectiveWsId = chosenWsId || currentWorkspaceId || "";
  const effectiveWsName = wsList.find((w) => w.id === effectiveWsId)?.name || "";
  const showPicker = wsList.length > 1;
  // dossier_note's target workspace is implied by property_id, so no
  // override is meaningful and no picker is shown. The other three
  // propose tools all let the user retarget.
  const allowRetarget = toolName !== "workspace_propose_dossier_note";

  // Frozen mode: post-resolution, show what was picked + what landed,
  // not a fresh row of clickable buttons. Status text is derived from
  // applied (the server's post-dispatch state) — if applied is missing
  // (e.g. live commit before /messages re-hydration filled it in), fall
  // back to a status keyed off selected.
  const resolved = resolvedSelected !== undefined;
  let statusText = "";
  let statusTone: "ok" | "muted" = "muted";
  if (resolved) {
    const sel = resolvedSelected ?? "";
    const applied = resolvedApplied ?? "";
    const chosenLabel = buttons.find((b) => b.value === sel)?.label ?? sel;
    switch (applied) {
      case "added_to_shortlist":
        statusText = "Added to your worklist";
        statusTone = "ok";
        break;
      case "workspace_updated":
        statusText = "Workspace brief updated";
        statusTone = "ok";
        break;
      case "memory_only":
        statusText = "Saved for this conversation only";
        statusTone = "ok";
        break;
      case "priority_synced":
        statusText = "Priority synced to your workspace";
        statusTone = "ok";
        break;
      case "already_present":
        statusText = "Already on your workspace";
        statusTone = "muted";
        break;
      case "dossier_note_added":
        statusText = "Added to dossier";
        statusTone = "ok";
        break;
      case "skipped":
      case "ignored":
        statusText = "Skipped (won't ask again for a while)";
        statusTone = "muted";
        break;
      default:
        // Live commit before backend echo: just say what they picked.
        statusText = chosenLabel ? `You picked: ${chosenLabel}` : "Decision recorded";
        statusTone = "muted";
    }
  }
  return (
    <div className="msg-in flex gap-3 py-1">
      <Avatar role="assistant" />
      <div className="min-w-0 flex-1 flex flex-col">
        <div className="mb-1.5 flex items-center gap-1.5" style={{ color: "var(--ai-accent)" }}>
          <span className="text-[11px] font-semibold tracking-[0.02em]">Proactive · ProProp</span>
        </div>
        <div className="rounded-md border border-border bg-surface-2 px-3 py-2 text-[13px]">
          <div className="mb-1 font-semibold">{title}</div>
          <div>{body}</div>
          {resolved ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <div
                className={
                  "inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[12px] " +
                  (statusTone === "ok"
                    ? "bg-accent/15 text-accent"
                    : "bg-surface-3 text-text-muted")
                }
              >
                {statusTone === "ok" ? "✓" : "·"} {statusText}
              </div>
              {(() => {
                // Deep-link into the affected workspace surface. Only
                // render when we have a destination AND something to
                // navigate to (a property for shortlist/dossier_note,
                // the workspace root for brief/priority edits).
                const wsId = resolvedWorkspaceId || "";
                if (!wsId) return null;
                let href = "";
                let label = "";
                switch (resolvedApplied) {
                  case "added_to_shortlist":
                  case "dossier_note_added":
                    if (resolvedPropertyId) {
                      href = `/workspace/${wsId}/shortlist/${resolvedPropertyId}`;
                      label = "Open dossier →";
                    }
                    break;
                  case "workspace_updated":
                  case "priority_synced":
                    href = `/workspace/${wsId}`;
                    label = "Open workspace →";
                    break;
                }
                if (!href) return null;
                return (
                  <Link
                    to={href}
                    className="text-[12px] font-medium text-accent underline-offset-2 hover:underline"
                  >
                    {label}
                  </Link>
                );
              })()}
            </div>
          ) : (
            <>
              {allowRetarget && effectiveWsName ? (
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px] text-text-muted">
                  <span>Destination:</span>
                  {showPicker ? (
                    <select
                      value={effectiveWsId}
                      onChange={(e) => setChosenWsId(e.target.value)}
                      disabled={disabled}
                      className="rounded border border-border bg-surface-3 px-1.5 py-0.5 text-[12px] text-text disabled:opacity-50"
                    >
                      {wsList.map((w) => (
                        <option key={w.id} value={w.id}>{w.name || "(unnamed)"}</option>
                      ))}
                    </select>
                  ) : (
                    <strong className="text-text">{effectiveWsName}</strong>
                  )}
                </div>
              ) : null}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {buttons.map((b) => (
                  <button
                    key={b.value}
                    type="button"
                    disabled={disabled}
                    onClick={() => onPick(b.value, allowRetarget ? effectiveWsId : undefined)}
                    className={
                      b.primary
                        ? "rounded-md bg-accent px-3 py-1 text-[12px] font-medium text-white transition hover:opacity-90 disabled:opacity-50"
                        : "rounded-md border border-border bg-surface-3 px-3 py-1 text-[12px] text-text transition hover:border-border-strong disabled:opacity-50"
                    }
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
